import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

export const CATALOG_MFG_LEAD_TIME_INFO = "This is used for purchase order notifications";
export const CATALOG_TOTAL_LEAD_TIME_INFO = "This is used for demand planning";
export const CATALOG_PROTECTED_INFO =
    "Tier 2 only. Protected = Yes when on-hand days of stock is less than MFG Lead Time + Transit Time, so the SKU would sell out before replenishment arrives. Protected SKUs cannot be discounted or put on sale (max discount 0%).";
export const CATALOG_MAX_DISCOUNT_PCT_INFO =
    "Set per demand tier in Settings → Catalog Settings → Defaults → Tier Discount Drivers. Forced to 0% when Protected = Yes. Blank when the tier has no driver configured.";
export const CATALOG_MAX_DISCOUNT_PRICE_INFO =
    "MSRP − (MSRP × Max Discount %). Equals MSRP when Protected = Yes, and blank when the tier has no discount driver.";
export const CATALOG_MSRP_INFO =
    "List / strikethrough price. Shopify compare-at (kits: Shopify price), manual when Shopify has none. Reference and Discount App basis only; not used to turn plan dollars into units.";
export const CATALOG_SHOPIFY_PLANNED_GROSS_PRICE_INFO =
    "Governed pre-discount selling price used to convert Demand Plan dollars into planned units. Default = Shopify standing variant price, versioned by effective month. Override only where the SKU structurally sells off its standing price.";
export const CATALOG_AMAZON_PLANNED_GROSS_PRICE_INFO =
    "Governed pre-discount Amazon selling price for unit planning. Derived from the Shopify Planned Gross Price until an Amazon-specific override is set. Always explicit and versioned.";

export function CatalogInfoTip({ text }) {
    const anchorRef = useRef(null);
    const tooltipRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, placement: "above" });

    const updatePosition = useCallback(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width ?? 0;
        const tooltipHeight = tooltipRef.current?.getBoundingClientRect().height ?? 0;
        const gap = 8;
        let left = rect.right - tooltipWidth;
        if (left < gap) {
            left = gap;
        }
        if (tooltipWidth > 0 && left + tooltipWidth > window.innerWidth - gap) {
            left = Math.max(gap, window.innerWidth - gap - tooltipWidth);
        }
        const hasRoomAbove = rect.top - gap - tooltipHeight >= gap;
        setPos({
            top: hasRoomAbove ? rect.top - gap : rect.bottom + gap,
            left,
            placement: hasRoomAbove ? "above" : "below",
        });
    }, []);

    useLayoutEffect(() => {
        if (!isHovered) return;
        updatePosition();
        const frame = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(frame);
    }, [isHovered, updatePosition, text]);

    useEffect(() => {
        if (!isHovered) return undefined;
        const handleReposition = () => updatePosition();
        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, { passive: true, capture: true });
        return () => {
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, { capture: true });
        };
    }, [isHovered, updatePosition]);

    return (
        <span
            ref={anchorRef}
            className="inline-flex shrink-0 -m-1 p-1 align-middle"
            onMouseDown={(event) => event.stopPropagation()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <InformationCircleIcon
                className={`h-3.5 w-3.5 transition-colors ${isHovered ? "text-gray-600" : "text-gray-400"}`}
                aria-label={text}
            />
            {isHovered
                ? createPortal(
                      <span
                          ref={tooltipRef}
                          role="tooltip"
                          className={`pointer-events-none fixed z-[500] w-max max-w-[min(20rem,calc(100vw-1rem))] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-normal normal-case leading-relaxed text-gray-700 shadow-lg ${
                              pos.placement === "above" ? "-translate-y-full" : ""
                          }`}
                          style={{ top: pos.top, left: pos.left }}
                      >
                          {text}
                      </span>,
                      document.body
                  )
                : null}
        </span>
    );
}
